/**
 * /expenses — Expense Reports redesigned with two paths in:
 *
 *   ┌─ "Snap or upload receipt" → AI OCR pre-fills the form, user reviews
 *   └─ "Enter manually"          → blank form, no AI dependency
 *
 * Manual entry is ALWAYS available. If OCR fails the user just types.
 * Every AI-pre-filled field has a small ✨ chip; editing the field clears
 * the chip so reviewers can see what was originally suggested vs. what was
 * confirmed by a human.
 *
 * Bottom of the page is the existing reports list with a Receipt + Source
 * column added; rows are clickable to re-open the form for editing (only
 * allowed while the report is not yet posted to AP).
 */
import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { $fetch } from "ofetch";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditIcon from "@mui/icons-material/Edit";

import { api, getToken } from "@/lib/api";
import {
  accountsListQueryOptions,
  partiesListQueryOptions,
  type AccountSummary,
} from "@/lib/queries";
import { ProjectSelect } from "@/components/ProjectSelect";
import { CostCodeSelect } from "@/components/CostCodeSelect";
import type { CostCode, Party, ProjectSummary } from "@/lib/types";
import { codeAndName } from "../lib/displayCode";

export const Route = createFileRoute("/_auth/expenses")({
  component: ExpensesPage,
});

// ── Types ──────────────────────────────────────────────────────
interface ExpenseLine {
  id: string;
  line_no: number;
  expense_date: string;
  category: string;
  description: string | null;
  merchant: string | null;
  amount: string;
  expense_account_id: string;
  project_id: string | null;
  cost_code_id: string | null;
  is_billable: boolean;
  receipt_url: string | null;
}

interface ExpenseReport {
  id: string;
  report_no: string;
  employee_id: string;
  period_start: string;
  period_end: string;
  status: string;
  description: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  total_amount: string;
  currency_code: string;
  ap_invoice_id: string | null;
  receipt_url: string | null;
  source: string;
  lines: ExpenseLine[];
}

interface ExpenseLineDraftWire {
  description: string;
  amount: string;
  account_code: string | null;
}

interface ExpenseDraftFromReceipt {
  receipt_file_id: string;
  receipt_url: string;
  vendor_name: string | null;
  expense_date: string | null;
  total: string | null;
  currency: string | null;
  tax_amount: string | null;
  payment_method: string | null;
  lines: ExpenseLineDraftWire[];
  confidence: number;
  raw_extraction: Record<string, unknown>;
}

const expenseReportsQueryOptions = () =>
  queryOptions({
    queryKey: ["expense-reports"],
    queryFn: () => api<ExpenseReport[]>("/api/saas/expense-reports"),
  });

const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  draft: "default",
  submitted: "warning",
  approved: "success",
  rejected: "error",
  reimbursed: "info",
};

const PAYMENT_METHODS = ["cash", "card", "check", "personal"] as const;
type PaymentMethod = (typeof PAYMENT_METHODS)[number];

function fmtMoney(v: string | number, currency = "USD") {
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
}

// Build a URL the browser can fetch directly. We append the JWT in a query
// string isn't safe; instead we use auth headers via api() but for <img>
// we sadly need the raw URL — so we expose a route that's behind auth
// middleware (cookie/session). Browser will hit it with same-origin and
// the existing Bearer header isn't present on <img>. So we render the
// receipt as an authenticated fetch into a blob URL.
function useReceiptBlob(receiptUrl: string | null | undefined): string | null {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked: string | null = null;
    setBlobUrl(null);
    if (!receiptUrl) return;
    const fileId = receiptUrl.replace(/^local-receipt:\/\//, "").split(".")[0];
    if (!fileId) return;
    const token = getToken();
    fetch(`/api/saas/expense-reports/files/${fileId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (!b) return;
        const url = URL.createObjectURL(b);
        revoked = url;
        setBlobUrl(url);
      })
      .catch(() => {
        /* swallow — preview is best-effort */
      });
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [receiptUrl]);
  return blobUrl;
}

// ─────────────────────────────────────────────────────────────
// Form state — small enough to keep flat instead of useReducer.
// `aiFields` tracks which fields were originally pre-filled by OCR so
// the ✨ chip disappears as soon as the user edits the value.
// ─────────────────────────────────────────────────────────────
interface LineFormState {
  description: string;
  amount: string;
  account_id: string;
  project_id: string | null;
  cost_code_id: string | null;
}

interface FormState {
  reportNo: string;
  employeeId: string;
  vendorName: string;
  expenseDate: string;
  description: string;
  currency: string;
  taxAmount: string;
  paymentMethod: PaymentMethod | "";
  receiptUrl: string | null;
  projectId: string | null;
  /** Job coding applied to lines that don't carry their own (JOB-COSTING-GATE:
   *  expense lines need project + cost code to post on construction entities). */
  costCode: CostCode | null;
  lines: LineFormState[];
}

const EMPTY_FORM: FormState = {
  reportNo: "",
  employeeId: "",
  vendorName: "",
  expenseDate: new Date().toISOString().slice(0, 10),
  description: "",
  currency: "USD",
  taxAmount: "",
  paymentMethod: "",
  receiptUrl: null,
  projectId: null,
  costCode: null,
  lines: [
    { description: "", amount: "", account_id: "", project_id: null, cost_code_id: null },
  ],
};

interface FormProps {
  open: boolean;
  onClose: () => void;
  initial?: FormState;
  /** When non-empty, these fields had AI-suggested values. Used for the ✨ badges. */
  aiFields?: Set<string>;
  /** OCR confidence (0–1). When provided we show a banner. */
  aiConfidence?: number | null;
  editingReportId?: string | null;
}

function ExpenseFormDialog({
  open,
  onClose,
  initial,
  aiFields,
  aiConfidence,
  editingReportId,
}: FormProps) {
  const qc = useQueryClient();

  // Reset state every time `initial` changes (i.e. when opened fresh)
  const [form, setForm] = useState<FormState>(initial ?? EMPTY_FORM);
  const [aiSet, setAiSet] = useState<Set<string>>(new Set(aiFields ?? []));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setForm(initial ?? EMPTY_FORM);
      setAiSet(new Set(aiFields ?? []));
      setError(null);
    }
  }, [open, initial, aiFields]);

  const employeesQ = useQuery(
    partiesListQueryOptions({ party_type: "employee", active_only: true }),
  );
  const accountsQ = useQuery(accountsListQueryOptions());

  const expenseAccounts: AccountSummary[] = useMemo(
    () =>
      (accountsQ.data ?? []).filter(
        (a) =>
          a.is_postable &&
          (a.account_type === "EXPENSE" || a.account_type === "expense"),
      ),
    [accountsQ.data],
  );

  const total = useMemo(
    () =>
      form.lines.reduce((sum, l) => {
        const n = parseFloat(l.amount);
        return isNaN(n) ? sum : sum + n;
      }, 0),
    [form.lines],
  );

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (aiSet.has(key as string)) {
      const next = new Set(aiSet);
      next.delete(key as string);
      setAiSet(next);
    }
  };

  const updateLine = (i: number, patch: Partial<LineFormState>) => {
    setForm((f) => {
      const lines = f.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
      return { ...f, lines };
    });
    // Editing any line clears the lines AI badge
    if (aiSet.has("lines")) {
      const next = new Set(aiSet);
      next.delete("lines");
      setAiSet(next);
    }
  };

  const addLine = () =>
    setForm((f) => ({
      ...f,
      lines: [
        ...f.lines,
        { description: "", amount: "", account_id: "", project_id: f.projectId,
          cost_code_id: f.costCode?.id ?? null },
      ],
    }));

  const removeLine = (i: number) =>
    setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const replaceReceipt = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const token = getToken();
      return $fetch<ExpenseDraftFromReceipt>(
        "/api/saas/expense-reports/extract-receipt",
        {
          method: "POST",
          body: fd,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        },
      );
    },
    onSuccess: (draft) => {
      // Just swap the receipt URL — don't blow away user-typed fields.
      setForm((f) => ({ ...f, receiptUrl: draft.receipt_url }));
    },
  });

  const submitMut = useMutation({
    mutationFn: async (action: "draft" | "submit") => {
      // Resolve account_id when the user hasn't picked one (default to first
      // expense account so the API doesn't 422 — they can fix later).
      const fallbackAccount = expenseAccounts[0]?.id;
      const lines = form.lines
        .filter((l) => l.amount && parseFloat(l.amount) > 0)
        .map((l, idx) => ({
          line_no: idx + 1,
          expense_date: form.expenseDate,
          category: form.description ? form.description.slice(0, 60) : "expense",
          description: l.description || null,
          merchant: form.vendorName || null,
          amount: l.amount,
          expense_account_id: l.account_id || fallbackAccount,
          project_id: l.project_id ?? form.projectId ?? null,
          cost_code_id: l.cost_code_id ?? form.costCode?.id ?? null,
          is_billable: false,
          receipt_url: form.receiptUrl,
          dimensions: form.taxAmount
            ? { tax_amount: form.taxAmount, payment_method: form.paymentMethod }
            : null,
        }));

      if (lines.length === 0) {
        throw new Error("Add at least one line with a positive amount.");
      }
      if (!form.employeeId) {
        throw new Error("Pick an employee.");
      }
      if (!fallbackAccount) {
        throw new Error("No expense accounts found. Add one in Chart of Accounts.");
      }

      const body = {
        report_no:
          form.reportNo ||
          `EXP-${new Date().toISOString().replace(/[-:T.Z]/g, "").slice(0, 14)}`,
        employee_id: form.employeeId,
        period_start: form.expenseDate,
        period_end: form.expenseDate,
        description: form.description || null,
        currency_code: form.currency || "USD",
        receipt_url: form.receiptUrl,
        source: aiFields && aiFields.size > 0 ? "receipt_ocr" : "manual",
        lines,
      };

      let result: ExpenseReport;
      if (editingReportId) {
        result = await api<ExpenseReport>(
          `/api/saas/expense-reports/${editingReportId}`,
          { method: "PATCH", body: { ...body, report_no: undefined } },
        );
      } else {
        result = await api<ExpenseReport>("/api/saas/expense-reports", {
          method: "POST",
          body,
        });
      }
      if (action === "submit" && result.status === "draft") {
        await api(`/api/saas/expense-reports/${result.id}/submit`, {
          method: "POST",
        });
      }
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["expense-reports"] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const receiptPreview = useReceiptBlob(form.receiptUrl);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <ReceiptIcon color="primary" />
        {editingReportId ? "Edit expense" : "New expense"}
        {aiConfidence != null && (
          <Chip
            size="small"
            color={
              aiConfidence >= 0.85
                ? "success"
                : aiConfidence >= 0.6
                  ? "warning"
                  : "error"
            }
            label={`AI confidence: ${
              aiConfidence >= 0.85 ? "HIGH" : aiConfidence >= 0.6 ? "MEDIUM" : "LOW"
            } — please review`}
            sx={{ ml: 1 }}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: form.receiptUrl ? "1fr 1fr" : "1fr",
            gap: 3,
          }}
        >
          {/* ── Form ── */}
          <Stack spacing={2}>
            <TextField
              label="Report #"
              size="small"
              value={form.reportNo}
              onChange={(e) => setField("reportNo", e.target.value)}
              placeholder="auto-generated if blank"
              disabled={!!editingReportId}
              fullWidth
            />
            <TextField
              select
              label="Employee"
              size="small"
              required
              value={form.employeeId}
              onChange={(e) => setField("employeeId", e.target.value)}
              fullWidth
            >
              <MenuItem value="">— pick —</MenuItem>
              {(employeesQ.data ?? []).map((p: Party) => (
                <MenuItem key={p.id} value={p.id}>
                  {codeAndName(p)}
                </MenuItem>
              ))}
            </TextField>

            <FieldWithBadge ai={aiSet.has("vendorName")}>
              <TextField
                label="Vendor / merchant"
                size="small"
                value={form.vendorName}
                onChange={(e) => setField("vendorName", e.target.value)}
                fullWidth
              />
            </FieldWithBadge>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
              <FieldWithBadge ai={aiSet.has("expenseDate")}>
                <TextField
                  label="Date"
                  size="small"
                  type="date"
                  value={form.expenseDate}
                  onChange={(e) => setField("expenseDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </FieldWithBadge>
              <FieldWithBadge ai={aiSet.has("currency")}>
                <TextField
                  label="Currency"
                  size="small"
                  value={form.currency}
                  onChange={(e) => setField("currency", e.target.value.toUpperCase())}
                  inputProps={{ maxLength: 3 }}
                  fullWidth
                />
              </FieldWithBadge>
              <FieldWithBadge ai={aiSet.has("taxAmount")}>
                <TextField
                  label="Tax amount"
                  size="small"
                  value={form.taxAmount}
                  onChange={(e) => setField("taxAmount", e.target.value)}
                  fullWidth
                />
              </FieldWithBadge>
            </Box>

            <FieldWithBadge ai={aiSet.has("paymentMethod")}>
              <TextField
                select
                label="Payment method"
                size="small"
                value={form.paymentMethod}
                onChange={(e) =>
                  setField("paymentMethod", e.target.value as PaymentMethod | "")
                }
                fullWidth
              >
                <MenuItem value="">—</MenuItem>
                {PAYMENT_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </FieldWithBadge>

            <TextField
              label="Description"
              size="small"
              multiline
              rows={2}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              fullWidth
            />

            <ProjectSelect
              value={null}
              onChange={(p: ProjectSummary | null) =>
                setField("projectId", p?.id ?? null)
              }
              label="Project (optional)"
            />

            <CostCodeSelect
              value={form.costCode}
              onChange={(cc) => setField("costCode", cc)}
              projectId={form.projectId}
              label="Cost code"
              placeholder="Search cost code (required to post on jobs)…"
            />

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                Lines
                {aiSet.has("lines") && (
                  <Chip
                    size="small"
                    icon={<AutoAwesomeIcon />}
                    label="AI-detected"
                    sx={{ ml: 1, bgcolor: "warning.light", color: "warning.dark" }}
                  />
                )}
              </Typography>
            </Divider>

            <Stack spacing={1}>
              {form.lines.map((l, i) => (
                <Box
                  key={i}
                  sx={{ display: "grid", gridTemplateColumns: "1fr 120px 200px 32px", gap: 1 }}
                >
                  <TextField
                    size="small"
                    placeholder="Description"
                    value={l.description}
                    onChange={(e) => updateLine(i, { description: e.target.value })}
                  />
                  <TextField
                    size="small"
                    placeholder="0.00"
                    value={l.amount}
                    onChange={(e) => updateLine(i, { amount: e.target.value })}
                    inputProps={{ inputMode: "decimal" }}
                  />
                  <TextField
                    select
                    size="small"
                    value={l.account_id}
                    onChange={(e) => updateLine(i, { account_id: e.target.value })}
                    placeholder="Account"
                  >
                    <MenuItem value="">— account —</MenuItem>
                    {expenseAccounts.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {codeAndName(a)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    size="small"
                    onClick={() => removeLine(i)}
                    disabled={form.lines.length <= 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={addLine}
                sx={{ alignSelf: "flex-start" }}
              >
                Add line
              </Button>
            </Stack>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Typography variant="body2" color="text.secondary">
                Total:&nbsp;
                <strong>{fmtMoney(total, form.currency)}</strong>
              </Typography>
            </Box>

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          </Stack>

          {/* ── Receipt preview pane ── */}
          {form.receiptUrl && (
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                RECEIPT
              </Typography>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.default",
                  minHeight: 360,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {receiptPreview ? (
                  form.receiptUrl.endsWith(".pdf") ? (
                    <iframe
                      src={receiptPreview}
                      title="Receipt"
                      style={{ width: "100%", height: 480, border: 0 }}
                    />
                  ) : (
                    <img
                      src={receiptPreview}
                      alt="Receipt"
                      style={{ maxWidth: "100%", maxHeight: 480 }}
                    />
                  )
                ) : (
                  <CircularProgress size={20} />
                )}
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={replaceReceipt.isPending}
                >
                  {replaceReceipt.isPending ? "Reading…" : "Replace receipt"}
                  <input
                    type="file"
                    hidden
                    accept="image/*,application/pdf"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) replaceReceipt.mutate(f);
                    }}
                  />
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => setField("receiptUrl", null)}
                >
                  Remove receipt
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="outlined"
          onClick={() => submitMut.mutate("draft")}
          disabled={submitMut.isPending}
        >
          Save as draft
        </Button>
        <Button
          variant="contained"
          onClick={() => submitMut.mutate("submit")}
          disabled={submitMut.isPending}
        >
          Submit for approval
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FieldWithBadge({
  ai,
  children,
}: {
  ai: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ position: "relative" }}>
      {children}
      {ai && (
        <Chip
          size="small"
          icon={<AutoAwesomeIcon sx={{ fontSize: 12 }} />}
          label="AI-detected"
          sx={{
            position: "absolute",
            top: -8,
            right: 8,
            height: 18,
            fontSize: 10,
            bgcolor: "warning.light",
            color: "warning.dark",
            "& .MuiChip-icon": { ml: 0.5, mr: -0.5 },
          }}
        />
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────
function ExpensesPage() {
  const qc = useQueryClient();
  const q = useQuery(expenseReportsQueryOptions());

  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState<FormState | undefined>();
  const [aiFields, setAiFields] = useState<Set<string> | undefined>();
  const [aiConfidence, setAiConfidence] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [actionError, setActionError] = useState<string | null>(null);
  const [ocrError, setOcrError] = useState<string | null>(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openManual = () => {
    setFormInitial(undefined);
    setAiFields(undefined);
    setAiConfidence(null);
    setEditingId(null);
    setFormOpen(true);
  };

  const openFromOcr = async (file: File) => {
    setOcrError(null);
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = getToken();
      const draft = await $fetch<ExpenseDraftFromReceipt>(
        "/api/saas/expense-reports/extract-receipt",
        {
          method: "POST",
          body: fd,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        },
      );
      const ai = new Set<string>();
      const init: FormState = { ...EMPTY_FORM };
      init.receiptUrl = draft.receipt_url;
      if (draft.vendor_name) {
        init.vendorName = draft.vendor_name;
        ai.add("vendorName");
      }
      if (draft.expense_date) {
        init.expenseDate = draft.expense_date;
        ai.add("expenseDate");
      }
      if (draft.currency) {
        init.currency = draft.currency;
        ai.add("currency");
      }
      if (draft.tax_amount) {
        init.taxAmount = draft.tax_amount;
        ai.add("taxAmount");
      }
      if (draft.payment_method) {
        init.paymentMethod = draft.payment_method as PaymentMethod;
        ai.add("paymentMethod");
      }
      if (draft.lines.length > 0) {
        init.lines = draft.lines.map((l) => ({
          description: l.description,
          amount: l.amount,
          account_id: "",
          project_id: null,
          cost_code_id: null,
        }));
        ai.add("lines");
      }
      setFormInitial(init);
      setAiFields(ai);
      setAiConfidence(draft.confidence);
      setEditingId(null);
      setFormOpen(true);
    } catch (e) {
      setOcrError(
        e instanceof Error
          ? `Couldn't read that receipt: ${e.message}. Use "Enter manually" instead.`
          : "Receipt OCR failed. Use 'Enter manually' instead.",
      );
    } finally {
      setOcrLoading(false);
    }
  };

  const openEdit = (r: ExpenseReport) => {
    if (r.ap_invoice_id) return; // posted → immutable
    const init: FormState = {
      reportNo: r.report_no,
      employeeId: r.employee_id,
      vendorName: r.lines[0]?.merchant ?? "",
      expenseDate: r.lines[0]?.expense_date ?? r.period_start,
      description: r.description ?? "",
      currency: r.currency_code,
      taxAmount: "",
      paymentMethod: "",
      receiptUrl: r.receipt_url,
      projectId: r.lines[0]?.project_id ?? null,
      costCode: null,  // per-line cost_code_id below preserves existing coding
      lines:
        r.lines.length > 0
          ? r.lines.map((l) => ({
              description: l.description ?? "",
              amount: l.amount,
              account_id: l.expense_account_id,
              project_id: l.project_id,
              cost_code_id: l.cost_code_id ?? null,
            }))
          : EMPTY_FORM.lines,
    };
    setFormInitial(init);
    setAiFields(new Set());
    setAiConfidence(null);
    setEditingId(r.id);
    setFormOpen(true);
  };

  const actionMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "submit" | "approve" | "post" }) => {
      const endpoint =
        action === "post"
          ? `/api/saas/expense-reports/${id}/post`
          : `/api/saas/expense-reports/${id}/${action}`;
      return api<ExpenseReport>(endpoint, { method: "POST" });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["expense-reports"] });
      setActionError(null);
    },
    onError: (e) => setActionError(String(e)),
  });

  const reports = q.data ?? [];
  const filtered =
    statusFilter === "all" ? reports : reports.filter((r) => r.status === statusFilter);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <ReceiptIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          Expenses
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Snap a receipt or type it in — your choice
        </Typography>
      </Box>

      {/* ── Two big entry cards ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
        <UploadCard
          loading={ocrLoading}
          onPick={() => fileInputRef.current?.click()}
          onDrop={(f) => void openFromOcr(f)}
        />
        <ManualCard onClick={openManual} />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void openFromOcr(f);
            e.target.value = "";
          }}
        />
      </Box>

      {ocrError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setOcrError(null)}>
          {ocrError}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* ── Filter bar ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Filter:
        </Typography>
        {["all", "draft", "submitted", "approved", "reimbursed", "rejected"].map((s) => (
          <Chip
            key={s}
            label={s}
            size="small"
            color={statusFilter === s ? "primary" : "default"}
            variant={statusFilter === s ? "filled" : "outlined"}
            onClick={() => setStatusFilter(s)}
          />
        ))}
      </Box>

      {/* ── Reports list ── */}
      {q.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: 600,
                    bgcolor: "action.hover",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  },
                }}
              >
                <TableCell>Report #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="center">Receipt</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align="center"
                    sx={{ py: 6, color: "text.secondary" }}
                  >
                    No expenses match. Use one of the cards above to add one.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => (
                <Fragment key={r.id}>
                  <TableRow
                    hover
                    sx={{ cursor: r.ap_invoice_id ? "default" : "pointer" }}
                    onClick={() => openEdit(r)}
                  >
                    <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {r.report_no}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
                      {r.period_end}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={r.status}
                        size="small"
                        color={STATUS_COLOR[r.status] ?? "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {r.source === "receipt_ocr" ? (
                        <Chip
                          size="small"
                          icon={<AutoAwesomeIcon sx={{ fontSize: 12 }} />}
                          label="receipt-ocr"
                          sx={{ bgcolor: "warning.light", color: "warning.dark" }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {r.source}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {r.receipt_url || r.lines.some((l) => l.receipt_url) ? (
                        <ReceiptIcon fontSize="small" color="action" />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {fmtMoney(r.total_amount, r.currency_code)}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {r.description ?? "—"}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {!r.ap_invoice_id && (
                          <IconButton size="small" onClick={() => openEdit(r)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {r.status === "draft" && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              actionMut.mutate({ id: r.id, action: "submit" })
                            }
                          >
                            Submit
                          </Button>
                        )}
                        {r.status === "submitted" && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() =>
                              actionMut.mutate({ id: r.id, action: "approve" })
                            }
                          >
                            Approve
                          </Button>
                        )}
                        {r.status === "approved" && !r.ap_invoice_id && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => actionMut.mutate({ id: r.id, action: "post" })}
                          >
                            Post to AP
                          </Button>
                        )}
                        {r.ap_invoice_id && (
                          <Typography variant="caption" color="success.main">
                            Posted ✓
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ExpenseFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initial={formInitial}
        aiFields={aiFields}
        aiConfidence={aiConfidence}
        editingReportId={editingId}
      />
    </Box>
  );
}

function UploadCard({
  loading,
  onPick,
  onDrop,
}: {
  loading: boolean;
  onPick: () => void;
  onDrop: (f: File) => void;
}) {
  const [over, setOver] = useState(false);
  return (
    <Paper
      variant="outlined"
      onClick={onPick}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onDrop(f);
      }}
      sx={{
        p: 4,
        cursor: "pointer",
        borderStyle: "dashed",
        borderWidth: 2,
        borderColor: over ? "primary.main" : "divider",
        bgcolor: over ? "action.hover" : "background.paper",
        textAlign: "center",
        transition: "all 0.15s",
        "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
      }}
    >
      {loading ? (
        <>
          <CircularProgress size={32} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Reading receipt…
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h4" sx={{ mb: 1 }}>
            📷
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>
            Snap or upload receipt
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Drag-drop or click — AI fills in the form, you review
          </Typography>
        </>
      )}
    </Paper>
  );
}

function ManualCard({ onClick }: { onClick: () => void }) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 4,
        cursor: "pointer",
        textAlign: "center",
        transition: "all 0.15s",
        "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
      }}
    >
      <Typography variant="h4" sx={{ mb: 1 }}>
        ✏️
      </Typography>
      <Typography variant="subtitle1" fontWeight={600}>
        Enter manually
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Type it in — works without any receipt
      </Typography>
    </Paper>
  );
}
